import type { FC } from 'react';
import type { FirstInputType } from './First';
import type { FormProps } from './V1';

import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { loadStripe as LoadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { First } from './First';
import { Second } from './Second';

import useSales from 'shared/hooks/useSales';
import useErrorLogger from 'shared/hooks/useErrorLogger';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';

import 'react-phone-number-input/style.css';
import { ApiService } from 'shared/helpers/api';
import { COLOR_BLUE_6 } from 'shared/helpers/colors';
import { shade, sanitizeRichText, firePixelEvent, generateAccessKey } from 'shared/helpers/utils';
import { defaultWebValue, filterBackgroundColor } from 'shared/helpers/website';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Wrapper } from '../Common';
import HeadlineOnly from '../HeadlineOnly';
import { stateCodes, TOP_USED_CURRENCIES } from 'shared/helpers/static';
import useSalesForm from 'shared/hooks/useSalesForm';
import { AxiosError } from 'axios';

const Container = styled.div<{ backgroundColor: string }>`
  display: flex;
  justify-content: center;
  padding: 80px 0 80px;
  position: relative;
  background: ${(props) => props.backgroundColor};

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 52px 0 60px;
  `}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const StyledWrapper = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 4;
  gap: 80px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    justify-content: center;
  `}
`;

const TitleContainer = styled.div<{
  isTitleEmpty?: boolean;
  isContentEmpty?: boolean;
  editable?: boolean;
}>`
  width: 47%;
  max-width: 47%;
  word-wrap: break-word;
  display: flex;
  flex-direction: column;
  ${({ isTitleEmpty, editable }) => (isTitleEmpty && editable ? `gap: 50px;` : '')}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    margin-bottom: 34px;
  `}
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: 60px;
  margin-bottom: 32px;
  line-height: 1.3em;
  position: relative;
  min-height: 40px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    font-size: 50px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    font-size: 36px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    font-size: 30px;
    margin-bottom: 16px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty && editable
      ? `
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: 24px;
  line-height: 1.35em;
  position: relative;
  min-height: 40px; /* Ensure there's enough height for content */

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    font-size: 22px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty && editable
      ? `
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const FormInner = styled.div`
  position: relative;
  padding: 46px 88px;
  z-index: 4;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 32px 28px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 52px 35px 72px;
  `}
`;

const FormBg = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  opacity: 0.75;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 10px;
`;

const LoadingWrapper = styled.div`
  padding: 20px;
  text-align: center;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const StepIndicator = styled.div`
  margin-bottom: 24px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: relative;
  z-index: 5;
`;

export const V2: FC<FormProps> = (props: FormProps) => {
  const {
    button = defaultWebValue,
    backgroundColor = COLOR_BLUE_6,
    type,
    stripe,
    title = defaultWebValue,
    content = defaultWebValue,
    sub_domain,
    textColor,
    meta,
    customBackgroundColor,
    oppositeBackgroundColor,
    order
  } = props;

  const [firstPhase, setFirstPhase] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [stripeConfig, setStripeConfig] = useState({ id: '', clientSecret: '' });
  const [customerData, setCustomerData] = useState<FirstInputType | null>();
  const [isVip, setIsVip] = useState(false);

  const {
    updateDownloadUrl,
    salesType,
    price,
    eventDetails,
    salesOrderCount,
    isUpsellVIP,
    upsellDetails,
    upsellPrice,
    isDashboard
  } = useSales();
  const { updateActiveElement, resetActiveElement, containerClassname, editable } =
    useWebsiteEditor();
  const { logError } = useErrorLogger();
  const { navigateToThankYou } = useSalesForm();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [orderData, setOrderData] = useState<FirstInputType>({
    phone: '',
    email: '',
    first_name: '',
    last_name: '',
    subscribe: false,
    city: '',
    street: '',
    apartment: '',
    zipcode: '',
    state: ''
  });

  // Accessibility improvements
  const titleAccessKey = generateAccessKey('t', order);
  const contentAccessKey = generateAccessKey('c', order);
  const skipLinkId = `skip-to-form-v2-${meta['accessorKey'] || 'main'}`;

  const handleFormError = (error: Error | unknown, userMessage?: string) => {
    const errorMessage =
      userMessage || (error instanceof Error ? error.message : 'An unexpected error occurred');

    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'assertive');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Form error: ${errorMessage}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 5000);

    logError(error);
  };

  const stripePromise = useMemo(() => {
    if (type === 'sales' && stripe)
      return LoadStripe(stripe.public_key, { stripeAccount: stripe.account });
    return null;
  }, [type, stripe]);

  const subHeadline = useMemo(() => {
    if (salesType === 'sales') {
      return 'We will email you the files after your purchase is complete.';
    }
    return 'It will be headed your way soon, just give us your email address.';
  }, [salesType]);

  const finalBackgroundColor = useMemo(
    () => customBackgroundColor || shade(backgroundColor, 0.7),
    [customBackgroundColor, backgroundColor]
  );

  const finalTextColor = useMemo(
    () => filterBackgroundColor(finalBackgroundColor, textColor),
    [finalBackgroundColor, textColor]
  );

  const handleFirst = async (values: FirstInputType) => {
    try {
      setIsLoading(true);

      // Announce form processing to screen readers
      const processingAnnouncement = document.createElement('div');
      processingAnnouncement.setAttribute('aria-live', 'polite');
      processingAnnouncement.setAttribute('aria-atomic', 'true');
      processingAnnouncement.className = 'sr-only';
      processingAnnouncement.textContent = 'Processing your information. Please wait...';
      document.body.appendChild(processingAnnouncement);

      const res = await ApiService.post(`/sales/page/${sub_domain}/order/`, {
        ...values,
        is_vip: values?.is_vip || isUpsellVIP
      });

      setOrderData({
        ...values,
        ...res.data.book_order,
        state: stateCodes[values.state as keyof typeof stateCodes] || values.state
      });

      // Clean up processing announcement
      document.body.removeChild(processingAnnouncement);

      if (res.data?.book_order?.type === 'amazon') {
        navigateToThankYou();
        return;
      }

      const clientSecret = res.data.client_secret;
      const id = String(res.data.book_order.uuid);
      setCustomerData(values);

      if (values.email) firePixelEvent('Lead', values.email);

      setStripeConfig({
        id,
        clientSecret
      });

      if ((price === '0' || Number(price) === 0) && res.data.book_order.status === 'delivered') {
        const response = await ApiService.get(`/sales/page/${sub_domain}/delivery/${id}/`);
        updateDownloadUrl(response.data.file_url);
        navigateToThankYou();
      } else {
        setFirstPhase(false);

        // Announce step progression
        const stepAnnouncement = document.createElement('div');
        stepAnnouncement.setAttribute('aria-live', 'polite');
        stepAnnouncement.setAttribute('aria-atomic', 'true');
        stepAnnouncement.className = 'sr-only';
        stepAnnouncement.textContent = 'Information saved. Now proceeding to payment details.';
        document.body.appendChild(stepAnnouncement);
        setTimeout(() => document.body.removeChild(stepAnnouncement), 3000);
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: { code: string }[] }>;
      const errors = axiosError.response?.data?.errors;
      if (errors && errors.length > 0 && errors[0].code === 'BOOKORDER_ERROR_0001') {
        navigateToThankYou();
      } else {
        handleFormError(
          error,
          'Failed to process your information. Please check your details and try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecond = () => {
    try {
      setIsLoading(true);

      if (customerData?.email) firePixelEvent('Purchase', customerData.email);

      const checkDeliveryStatus = setInterval(async () => {
        try {
          const params = {
            email: customerData?.email,
            uuid: stripeConfig.id,
            sub_domain
          };
          const status = await ApiService.get(`/sales/page/${sub_domain}/status/`, { params });

          if (status.data.status === 'delivered') {
            clearInterval(checkDeliveryStatus);
            const response = await ApiService.get(
              `/sales/page/${sub_domain}/delivery/${stripeConfig.id}/`
            );

            updateDownloadUrl(response.data.file_url);
            setIsLoading(false);
            navigateToThankYou(stripeConfig.id);
            setFirstPhase(false);
          }
        } catch (error) {
          clearInterval(checkDeliveryStatus);
          handleFormError(error, 'Failed to check payment status. Please contact support.');
          setIsLoading(false);
        }
      }, 2000);
    } catch (error) {
      handleFormError(error, 'Payment processing failed. Please try again.');
      setIsLoading(false);
    }
  };

  // Event registration full handling
  if (
    !editable &&
    Number(eventDetails?.peopleLimit) &&
    Number(eventDetails?.peopleLimit) <= salesOrderCount
  ) {
    return (
      <HeadlineOnly.V1
        {...props}
        title={{
          id: title.id,
          val: 'Sorry, registration is full'
        }}
        customBackgroundColor={customBackgroundColor || '#fff'}
      />
    );
  }

  const currentStep = firstPhase ? 1 : 2;
  const totalSteps = type === 'sales' ? 2 : 1;

  return (
    <Container
      id="form"
      backgroundColor={finalBackgroundColor}
      className={containerClassname}
      role="main"
      aria-labelledby={title.id}>
      {/* Skip link for keyboard navigation */}
      <VisuallyHidden>
        <a href={`#${skipLinkId}`}>Skip to form content</a>
      </VisuallyHidden>

      <StyledWrapper id={skipLinkId}>
        <TitleContainer
          editable={isDashboard}
          isTitleEmpty={isTitleEmpty}
          isContentEmpty={isContentEmpty}
          role="region"
          aria-label="Form introduction">
          {/* Step indicator for multi-step forms */}
          {totalSteps > 1 && (
            <StepIndicator role="status" aria-label={`Step ${currentStep} of ${totalSteps}`}>
              <VisuallyHidden>
                Form progress: Step {currentStep} of {totalSteps}
              </VisuallyHidden>
              <div style={{ color: finalTextColor, fontSize: '14px', fontWeight: 'bold' }}>
                Step {currentStep} of {totalSteps}:{' '}
                {firstPhase ? 'Your Information' : 'Payment Details'}
              </div>
            </StepIndicator>
          )}

          <Title
            id={title?.id}
            style={{
              color: finalTextColor,
              display: sanitizeRichText(title.val, editable, true) || editable ? 'block' : 'none'
            }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleFormError(error, 'Failed to edit title');
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': "It's almost yours!",
                  editable: true
                }
              : {
                  'aria-label': isTitleEmpty || !title.val?.trim() ? 'Form title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this form title`,
                  tabIndex: 0
                })}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, editable, true)
                ? sanitizeRichText(title.val, editable, false, "<p>It's almost yours!</p>")
                : '<p></br><p>'
            }}
          />

          <Description
            id={content?.id}
            style={{
              color: finalTextColor,
              display: sanitizeRichText(content.val, editable, true) || editable ? 'block' : 'none'
            }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleFormError(error, 'Failed to edit description');
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': subHeadline,
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Form description' : undefined,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this form description`,
                  tabIndex: 0
                })}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(content.val, editable, true)
                ? sanitizeRichText(content.val, editable, false, subHeadline)
                : '<p><br></p>'
            }}
          />

          {/* VIP upsell information */}
          {isVip && upsellDetails?.product_name && (
            <Description style={{ color: finalTextColor }} aria-label="Upsell product information">
              The {upsellDetails.product_name}{' '}
              {TOP_USED_CURRENCIES?.[upsellDetails?.currency as keyof typeof TOP_USED_CURRENCIES] ||
                upsellDetails?.currency}
              {upsellPrice}
            </Description>
          )}
        </TitleContainer>

        <FormInner role="region" aria-label={firstPhase ? 'Information form' : 'Payment form'}>
          {isLoading ? (
            <LoadingWrapper role="status" aria-label="Processing request" aria-live="polite">
              <VisuallyHidden>
                {firstPhase ? 'Processing your information...' : 'Processing payment...'}
              </VisuallyHidden>
              <Center>
                <CircularProgress color="inherit" aria-hidden="true" size={40} />
              </Center>
            </LoadingWrapper>
          ) : !firstPhase && stripePromise ? (
            <div
              role="region"
              aria-label="Payment information"
              aria-describedby="payment-instructions-v2">
              <VisuallyHidden id="payment-instructions-v2">
                Complete your payment using the secure payment form. Your payment information is
                encrypted and protected.
              </VisuallyHidden>
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: stripeConfig.clientSecret }}>
                <Second
                  orderData={orderData}
                  backgroundColor={backgroundColor}
                  callback={handleSecond}
                  textColor={textColor}
                  oppositeBackgroundColor={oppositeBackgroundColor}
                  order={order}
                />
              </Elements>
            </div>
          ) : (
            <div
              role="region"
              aria-label="Personal information form"
              aria-describedby="form-instructions-v2">
              <VisuallyHidden id="form-instructions-v2">
                Please fill out the form with your information to continue.
              </VisuallyHidden>
              <First
                setIsVip={setIsVip}
                backgroundColor={backgroundColor}
                callback={handleFirst}
                textColor={textColor}
                button={button}
                meta={meta}
                oppositeBackgroundColor={oppositeBackgroundColor}
                order={order}
              />
            </div>
          )}

          {/* Background styling element */}
          <FormBg
            style={{
              backgroundColor: oppositeBackgroundColor,
              opacity: oppositeBackgroundColor ? '1' : '0.75'
            }}
            aria-hidden="true"
          />
        </FormInner>
      </StyledWrapper>
    </Container>
  );
};
